import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PerformRequest } from "../hooks/usePerformRequest";
import { Endpoints } from "../lib/endpoints";
import { HttpCodes } from "../lib/data";
import { AdminProfile, AdminLoginResponse } from "../lib/types";

interface AdminContextProps {
  admin: AdminProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  login: (payload: { email: string; password: string }) => Promise<{
    success: boolean;
    message?: string;
    requiresTwoFactor?: boolean;
    email?: string;
  }>;
  verifyTwoFactor: (payload: { email: string; otp: string }) => Promise<{
    success: boolean;
    message?: string;
  }>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  resendOTP: (email: string) => Promise<{ success: boolean; message?: string }>;
}

const AdminContext = createContext<AdminContextProps>({
  admin: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  login: async () => ({ success: false, message: "Not initialized" }),
  verifyTwoFactor: async () => ({ success: false, message: "Not initialized" }),
  logout: async () => undefined,
  refreshProfile: async () => undefined,
  resendOTP: async () => ({ success: false, message: "Not initialized" }),
});

export const AdminProvider = ({ children }: { children: React.ReactNode }) => {
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const persistToken = (value: string | null) => {
    if (value) {
      localStorage.setItem("t", value);
    } else {
      localStorage.removeItem("t");
    }
    setToken(value);
  };

  const refreshProfile = useCallback(async () => {
    const storedToken = localStorage.getItem("t");
    if (!storedToken) {
      setAdmin(null);
      setIsInitialized(true);
      return;
    }

    setToken(storedToken);
    setIsLoading(true);
    const { status, data } = await PerformRequest<AdminProfile>({
      method: Endpoints.GetAdminProfile().method,
      route: Endpoints.GetAdminProfile().route,
    });

    const profile = (data as any)?.data as AdminProfile | undefined;

    if (status === HttpCodes.Success && profile) {
      setAdmin(profile);
    } else {
      persistToken(null);
      setAdmin(null);
    }

    setIsLoading(false);
    setIsInitialized(true);
  }, []);

  const login = useCallback(
    async (payload: { email: string; password: string }) => {
      setIsLoading(true);
      const { status, data } = await PerformRequest<AdminLoginResponse>({
        method: Endpoints.GetLoginAdmin().method,
        route: Endpoints.GetLoginAdmin().route,
        data: payload,
        doNotUseAuthorization: true,
      });

      const responseData = data as unknown as AdminLoginResponse;
      if (
        status === HttpCodes.Success &&
        (responseData?.data as any)?.requiresTwoFactor
      ) {
        setIsLoading(false);
        return {
          success: true,
          message:
            responseData?.message || "Please check your email for the OTP.",
          requiresTwoFactor: true,
          email: (responseData?.data as any)?.email,
        };
      }

      const tokenFromApi = (responseData?.data as any)?.token;
      const adminFromApi = (responseData?.data as any)?.admin as AdminProfile;

      if (
        status === HttpCodes.Success &&
        tokenFromApi &&
        adminFromApi &&
        responseData
      ) {
        persistToken(tokenFromApi);
        setAdmin(adminFromApi);
        // Refresh from API to ensure latest profile shape
        await refreshProfile();
        setIsLoading(false);
        setIsInitialized(true);
        return { success: true, message: responseData?.message };
      }

      setIsLoading(false);
      return {
        success: false,
        message:
          status === HttpCodes.Unauthorized
            ? "Invalid credentials"
            : (responseData?.message as string) ||
              "Unable to login, please try again",
      };
    },
    [],
  );

  const logout = useCallback(async () => {
    setIsLoading(true);
    await PerformRequest({
      method: Endpoints.GetAdminLogout().method,
      route: Endpoints.GetAdminLogout().route,
    });
    persistToken(null);
    setAdmin(null);
    setIsLoading(false);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    refreshProfile().catch(() => setIsInitialized(true));
  }, [refreshProfile]);

  const verifyTwoFactor = useCallback(
    async (payload: { email: string; otp: string }) => {
      setIsLoading(true);
      const { status, data } = await PerformRequest<AdminLoginResponse>({
        method: Endpoints.GetVerifyTwoFactor().method,
        route: Endpoints.GetVerifyTwoFactor().route,
        data: payload,
        doNotUseAuthorization: true,
      });

      const responseData = data as unknown as AdminLoginResponse;
      const tokenFromApi = (responseData?.data as any)?.token;
      const adminFromApi = (responseData?.data as any)?.admin as AdminProfile;

      if (
        status === HttpCodes.Success &&
        tokenFromApi &&
        adminFromApi &&
        responseData
      ) {
        persistToken(tokenFromApi);
        setAdmin(adminFromApi);
        // Refresh from API to ensure latest profile shape
        await refreshProfile();
        setIsLoading(false);
        setIsInitialized(true);
        return { success: true, message: responseData?.message };
      }

      setIsLoading(false);
      return {
        success: false,
        message:
          status === HttpCodes.Unauthorized
            ? "Invalid OTP"
            : (responseData?.message as string) ||
              "Unable to verify OTP, please try again",
      };
    },
    [refreshProfile],
  );

  const resendOTP = useCallback(async (email: string) => {
    setIsLoading(true);
    const { status, data } = await PerformRequest({
      method: Endpoints.ResendOTP().method,
      route: Endpoints.ResendOTP().route,
      data: { email },
      doNotUseAuthorization: true,
    });

    setIsLoading(false);
    return {
      success: status === HttpCodes.Success,
      message:
        (data as any)?.message ||
        (status === HttpCodes.Success
          ? "OTP resent successfully"
          : "Failed to resend OTP"),
    };
  }, []);

  const value = useMemo(
    () => ({
      admin,
      token,
      isAuthenticated: !!token && !!admin,
      isLoading,
      isInitialized,
      login,
      verifyTwoFactor,
      logout,
      refreshProfile,
      resendOTP,
    }),
    [
      admin,
      token,
      isLoading,
      isInitialized,
      login,
      verifyTwoFactor,
      logout,
      refreshProfile,
      resendOTP,
    ],
  );

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};

export const useAdminContext = () => useContext(AdminContext);
